import { IDcaAllocationUpdate } from "@/models/dcaAllocation/dcaAllocation.types"
import { queryClient } from "@/api/utils/useQuery"
import { BackofficeRestMutations } from "@/api/backofficeRest/mutations"
import { BackofficeRestQueries } from "@/api/backofficeRest/queries"
import {
  UpdateDcaAllocationConfigAccountState,
  UpdateDcaAllocationConfigOccupier,
} from "@/api/backofficeRest/generated/model"

/**
 * Writes a split back for one (occupier, accountState, placementNumber).
 *
 * Two contract details worth keeping in view:
 *
 *  - Full replace, not patch. Any agency absent from `allocations` is deleted
 *    from that step.
 *  - Clearing a step means sending an EMPTY array. A list of agencies at 0 would
 *    total 0 and be rejected by the gateway's validation, which requires exactly
 *    100 for any non-empty list. `percentage: 0` is for retiring one agency while
 *    the others still total 100.
 *
 * A rejected write surfaces as a BackofficeApiError, so `save` propagates the failure and
 * skips the refetch below rather than confirming a write the gateway threw away.
 */

export interface IDcaAllocationRequestBody {
  allocations: { dca: string; percentage: number }[]
}

export const serialiseUpdate = (
  update: IDcaAllocationUpdate,
): IDcaAllocationRequestBody => ({
  // A cleared step sends [] rather than zeros — see the note above.
  allocations: update.allocations.map(({ dca, percentage }) => ({
    dca,
    percentage,
  })),
})

class DcaAllocationMutatorClass {
  async save(update: IDcaAllocationUpdate): Promise<void> {
    await BackofficeRestMutations.dcaAllocation.updateConfig().mutationFn({
      // The segment and placement identify the row via query parameters; only the
      // allocations travel in the body, so the same fact is not stated twice.
      params: {
        occupier:
          update.occupier as unknown as UpdateDcaAllocationConfigOccupier,
        accountState:
          update.accountState as unknown as UpdateDcaAllocationConfigAccountState,
        placementNumber: update.placementNumber,
      },
      body: serialiseUpdate(update),
    })

    // Refetch after the write, not before, or the matrix reloads the old split. The key is
    // taken from the registry entry that registers the query rather than written out here,
    // so invalidation cannot drift from where the query is actually keyed.
    await queryClient.invalidateQueries({
      queryKey: BackofficeRestQueries.dcaAllocation.config.queryKey,
    })
  }
}

export const DcaAllocationMutator = new DcaAllocationMutatorClass()
